import { ErrorCodes } from '@shared/constants/error-codes';

/**
 * Распарсенная ошибка от бэкенда (RFC 9457 `application/problem+json`).
 *
 * Машинный `code` — основной канал идентификации; локализация — на клиенте
 * через `i18n/ru/errors.json` по `code`. Для отдельных кодов
 * (`FEE_TARIFF_GRID_INVALID`) бэк дополнительно кладёт человекочитаемую
 * причину в `message` — показываем её как есть рядом с локализованным текстом.
 */
export type ApiError = {
  status: number;
  code: string;
  message?: string;
  path?: string;
  timestamp?: string;
  params?: Record<string, string | number | boolean>;
  fields?: { field: string; code: string }[];
};

/**
 * Разбирает body ответа об ошибке в `ApiError`.
 *
 * Если body не похоже на `application/problem+json` — возвращает
 * fallback с `code = UNKNOWN_ERROR`. Сетевая ошибка (нет ответа) —
 * `NETWORK_ERROR`.
 *
 * @param raw тело ответа (объект, строка, что угодно) либо `undefined` для сетевой ошибки
 * @param status HTTP-статус (для случаев, когда `raw` не содержит `status`)
 */
export function parseApiError(raw: unknown, status: number | undefined): ApiError {
  if (raw === undefined || raw === null) {
    return { status: status ?? 0, code: ErrorCodes.NETWORK };
  }
  if (typeof raw === 'object' && 'code' in raw) {
    const obj = raw as Partial<ApiError>;
    return {
      status: obj.status ?? status ?? 0,
      code: obj.code ?? ErrorCodes.UNKNOWN,
      ...(typeof obj.message === 'string' && { message: obj.message }),
      ...(obj.path !== undefined && { path: obj.path }),
      ...(obj.timestamp !== undefined && { timestamp: obj.timestamp }),
      ...(obj.params !== undefined && { params: obj.params }),
      ...(obj.fields !== undefined && { fields: obj.fields }),
    };
  }
  return { status: status ?? 0, code: ErrorCodes.UNKNOWN };
}
